import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Package, Search, Download, X, Truck, Send, Ban, Archive, ArchiveRestore,
  StickyNote, RotateCw, ChevronRight, CheckCircle2, Clock, Mail, History, Undo2,
} from "lucide-react";
import api, { eur, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUS_BADGE = {
  confirmed: "bg-gold/20 text-copper",
  preparation: "bg-blue-100 text-blue-700",
  shipped: "bg-purple-100 text-purple-700",
  delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
  refused: "bg-red-100 text-red-700",
  refunded: "bg-orange-100 text-orange-700",
};

const ACTIVE_STATUSES = ["confirmed", "preparation", "shipped"];
const ARCHIVED_STATUSES = ["delivered", "cancelled", "refused", "refunded"];

function StatusBadge({ status, labelMap }) {
  return (
    <span className={`text-xs px-2.5 py-1 rounded-sm whitespace-nowrap ${STATUS_BADGE[status] || "bg-sand text-graphite"}`}>
      {labelMap[status] || status}
    </span>
  );
}

function Kpi({ label, value, active, onClick, accent }) {
  return (
    <button
      onClick={onClick}
      className={`text-left bg-white border rounded-sm p-4 transition-colors ${
        active ? "border-copper" : "border-stone hover:border-graphite"
      }`}
    >
      <p className={`text-[10px] uppercase tracking-[0.2em] ${accent || "text-ash"}`}>{label}</p>
      <p className="font-serif text-3xl text-ink mt-1">{value}</p>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Detail drawer                                                       */
/* ------------------------------------------------------------------ */
function OrderDrawer({ orderId, meta, onClose, onChanged }) {
  const [order, setOrder] = useState(null);
  const [tab, setTab] = useState("actions");
  const [modal, setModal] = useState(null); // 'status' | 'tracking' | 'refuse' | 'message' | 'note'

  const load = useCallback(async () => {
    if (!orderId) return;
    const { data } = await api.get(`/admin/orders/${orderId}`);
    setOrder(data);
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  if (!orderId) return null;

  const statusMap = Object.fromEntries((meta?.statuses || []).map((s) => [s.value, s.label]));
  const carrierMap = Object.fromEntries((meta?.carriers || []).map((c) => [c.value, c.label]));

  const refresh = async () => {
    await load();
    onChanged?.();
  };

  const archive = async () => {
    await api.post(`/admin/orders/${orderId}/archive`);
    toast.success("Commande archivée");
    refresh();
  };
  const unarchive = async () => {
    await api.post(`/admin/orders/${orderId}/unarchive`);
    toast.success("Commande désarchivée");
    refresh();
  };
  const resend = async () => {
    try {
      await api.post(`/admin/orders/${orderId}/resend-confirmation`);
      toast.success("Email de confirmation renvoyé");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteNote = async (noteId) => {
    if (!window.confirm("Supprimer cette note ?")) return;
    await api.delete(`/admin/orders/${orderId}/notes/${noteId}`);
    refresh();
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-ink/50" onClick={onClose} />
      <aside className="relative bg-white w-full md:w-[680px] h-full overflow-y-auto shadow-2xl">
        {!order ? (
          <div className="p-10 text-center text-graphite">Chargement…</div>
        ) : (
          <>
            <header className="sticky top-0 bg-white border-b border-stone p-5 flex items-start justify-between z-10">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-ash">Commande</p>
                <h2 className="font-serif text-2xl text-ink mt-1">#{order.id.slice(0, 8).toUpperCase()}</h2>
                <p className="text-xs text-graphite mt-1">
                  {new Date(order.created_at).toLocaleString("fr-FR")} · {eur(order.total)}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <StatusBadge status={order.status} labelMap={statusMap} />
                  {order.archived && (
                    <span className="text-[10px] uppercase tracking-[0.15em] text-ash">Archivée</span>
                  )}
                </div>
              </div>
              <button onClick={onClose} className="text-graphite hover:text-ink p-1" data-testid="drawer-close">
                <X className="h-5 w-5" />
              </button>
            </header>

            {/* Tabs */}
            <div className="border-b border-stone flex">
              {[
                { id: "actions", label: "Actions" },
                { id: "details", label: "Détails" },
                { id: "timeline", label: `Historique (${(order.status_history?.length || 0) + (order.admin_notes?.length || 0)})` },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`drawer-tab-${t.id}`}
                  className={`px-5 py-3 text-xs uppercase tracking-[0.15em] border-b-2 ${
                    tab === t.id ? "border-copper text-ink" : "border-transparent text-ash"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="p-5 space-y-6">
              {tab === "actions" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <ActionBtn icon={Truck} label="Définir suivi & expédier" onClick={() => setModal("tracking")} testid="action-tracking" />
                    <ActionBtn icon={RotateCw} label="Changer le statut" onClick={() => setModal("status")} testid="action-status" />
                    <ActionBtn icon={Send} label="Message au client" onClick={() => setModal("message")} testid="action-message" />
                    <ActionBtn icon={Ban} label="Refuser la commande" onClick={() => setModal("refuse")} danger testid="action-refuse" />
                    {order.payment_status === "paid" && order.status !== "refunded" && (
                      <ActionBtn icon={Undo2} label="Rembourser (Stripe)" onClick={() => setModal("refund")} danger testid="action-refund" />
                    )}
                    <ActionBtn icon={Mail} label="Renvoyer la confirmation" onClick={resend} testid="action-resend" />
                    <ActionBtn icon={StickyNote} label="Ajouter une note interne" onClick={() => setModal("note")} testid="action-note" />
                    {order.archived ? (
                      <ActionBtn icon={ArchiveRestore} label="Désarchiver" onClick={unarchive} testid="action-unarchive" />
                    ) : (
                      <ActionBtn icon={Archive} label="Archiver" onClick={archive} testid="action-archive" />
                    )}
                  </div>

                  {order.tracking_number && (
                    <div className="bg-sand border border-stone rounded-sm p-4 text-sm">
                      <p className="text-[10px] uppercase tracking-[0.15em] text-ash mb-2">Suivi colis</p>
                      <p className="text-ink"><strong>{carrierMap[order.carrier] || order.carrier}</strong> · {order.tracking_number}</p>
                      {order.tracking_url && (
                        <a href={order.tracking_url} target="_blank" rel="noreferrer" className="text-copper text-xs underline mt-1 inline-block">
                          Ouvrir le suivi →
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}

              {tab === "details" && (
                <>
                  <Section title="Articles">
                    {order.items?.map((i) => (
                      <div key={i.product_id} className="flex items-center gap-3 py-1.5">
                        {i.image && <img src={i.image} alt={i.name} className="h-10 w-9 object-cover bg-stone rounded-sm" />}
                        <span className="flex-1 text-sm text-ink">{i.name} × {i.quantity}</span>
                        <span className="font-mono text-sm text-graphite">{eur(i.line_total || (i.price || 0) * i.quantity)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between border-t border-stone mt-3 pt-3 font-mono text-ink">
                      <span>Total</span><span>{eur(order.total)}</span>
                    </div>
                  </Section>
                  <Section title="Livraison">
                    <div className="text-sm text-graphite space-y-1">
                      <p className="text-ink font-medium">{order.shipping?.full_name || order.customer_name}</p>
                      <p>{order.shipping?.email || order.customer_email}</p>
                      <p>{order.shipping?.address}</p>
                      <p>{order.shipping?.postal_code} {order.shipping?.city}</p>
                      <p>{order.shipping?.country}</p>
                      {order.shipping?.phone && <p>📞 {order.shipping.phone}</p>}
                    </div>
                  </Section>
                  <Section title="Notes internes">
                    {(order.admin_notes || []).length === 0 ? (
                      <p className="text-sm text-graphite italic">Aucune note pour le moment.</p>
                    ) : (
                      <ul className="space-y-2">
                        {order.admin_notes.map((n) => (
                          <li key={n.id} className="bg-sand border border-stone rounded-sm p-3 text-sm group relative">
                            <p className="text-ink whitespace-pre-wrap">{n.text}</p>
                            <p className="text-[11px] text-ash mt-1.5">
                              {n.by_name} · {new Date(n.created_at).toLocaleString("fr-FR")}
                            </p>
                            <button
                              onClick={() => deleteNote(n.id)}
                              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-ash hover:text-red-600"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Section>
                </>
              )}

              {tab === "timeline" && (
                <Timeline order={order} statusMap={statusMap} />
              )}
            </div>
          </>
        )}

        {/* Modals */}
        {modal === "status" && <StatusModal order={order} meta={meta} onClose={() => setModal(null)} onDone={() => { setModal(null); refresh(); }} />}
        {modal === "tracking" && <TrackingModal order={order} meta={meta} onClose={() => setModal(null)} onDone={() => { setModal(null); refresh(); }} />}
        {modal === "refuse" && <RefuseModal order={order} meta={meta} onClose={() => setModal(null)} onDone={() => { setModal(null); refresh(); }} />}
        {modal === "refund" && <RefundModal order={order} onClose={() => setModal(null)} onDone={() => { setModal(null); refresh(); }} />}
        {modal === "message" && <MessageModal order={order} meta={meta} onClose={() => setModal(null)} onDone={() => { setModal(null); refresh(); }} />}
        {modal === "note" && <NoteModal order={order} onClose={() => setModal(null)} onDone={() => { setModal(null); refresh(); }} />}
      </aside>
    </div>
  );
}

function ActionBtn({ icon: Icon, label, onClick, danger, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-3 px-4 py-3 border rounded-sm text-sm text-left transition-colors ${
        danger ? "border-red-200 text-red-700 hover:bg-red-50" : "border-stone hover:bg-sand"
      }`}
    >
      <Icon className="h-4 w-4 shrink-0" strokeWidth={1.6} />
      <span className="flex-1">{label}</span>
      <ChevronRight className="h-3.5 w-3.5 text-ash" />
    </button>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-ash mb-3">{title}</p>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Timeline                                                            */
/* ------------------------------------------------------------------ */
function Timeline({ order, statusMap }) {
  const items = [];
  items.push({
    kind: "created",
    at: order.created_at,
    title: "Commande passée",
    detail: `${order.customer_name || order.shipping?.full_name || "Client"} · ${eur(order.total)}`,
  });
  (order.status_history || []).forEach((h) => {
    items.push({
      kind: "status",
      at: h.created_at,
      title: `Statut : ${statusMap[h.status] || h.status}`,
      detail: (
        <>
          {h.reason_label && <p className="text-graphite text-sm">Motif : <strong>{h.reason_label}</strong></p>}
          {h.message && <p className="text-graphite text-sm whitespace-pre-wrap mt-1">« {h.message} »</p>}
          {h.extra?.tracking_number && (
            <p className="text-graphite text-sm mt-1">Suivi : {h.extra.carrier} {h.extra.tracking_number}</p>
          )}
          <p className="text-[11px] text-ash mt-1">
            par {h.by_name || h.by} {h.email_sent && <span className="text-green-700">· email envoyé ✓</span>}
          </p>
        </>
      ),
    });
  });
  (order.admin_notes || []).forEach((n) => {
    items.push({
      kind: "note",
      at: n.created_at,
      title: "Note interne",
      detail: (
        <>
          <p className="text-graphite text-sm whitespace-pre-wrap">{n.text}</p>
          <p className="text-[11px] text-ash mt-1">par {n.by_name}</p>
        </>
      ),
    });
  });
  items.sort((a, b) => new Date(a.at) - new Date(b.at));

  return (
    <div className="relative pl-5 border-l-2 border-stone space-y-5">
      {items.map((it, idx) => (
        <div key={idx} className="relative">
          <span className={`absolute -left-[27px] top-1.5 h-3 w-3 rounded-full border-2 border-cream ${
            it.kind === "created" ? "bg-copper" : it.kind === "note" ? "bg-ash" : "bg-ink"
          }`} />
          <p className="text-xs uppercase tracking-[0.1em] text-ash">{new Date(it.at).toLocaleString("fr-FR")}</p>
          <p className="text-ink font-medium mt-1">{it.title}</p>
          <div className="mt-1">{typeof it.detail === "string" ? <p className="text-graphite text-sm">{it.detail}</p> : it.detail}</div>
        </div>
      ))}
      {items.length === 0 && <p className="text-graphite italic">Aucun événement.</p>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Modals                                                              */
/* ------------------------------------------------------------------ */
function ModalShell({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/60" onClick={onClose} />
      <div className="relative bg-white rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <header className="flex items-center justify-between border-b border-stone p-5">
          <h3 className="font-serif text-xl text-ink">{title}</h3>
          <button onClick={onClose} className="text-graphite hover:text-ink"><X className="h-5 w-5" /></button>
        </header>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

const FieldLabel = ({ children }) => <label className="text-[11px] uppercase tracking-[0.15em] text-ash">{children}</label>;
const SubmitBtn = ({ children, loading, danger, testid }) => (
  <button
    type="submit"
    disabled={loading}
    data-testid={testid}
    className={`px-5 py-3 text-xs uppercase tracking-[0.15em] disabled:opacity-50 ${
      danger ? "bg-red-700 text-white" : "bg-ink text-cream"
    }`}
  >
    {loading ? "Envoi…" : children}
  </button>
);

function StatusModal({ order, meta, onClose, onDone }) {
  const [status, setStatus] = useState(order.status || "confirmed");
  const [message, setMessage] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [loading, setLoading] = useState(false);

  // hide 'shipped' (tracking modal), 'refused' (refuse modal) et 'refunded'
  // (action dédiée « Rembourser » qui déclenche un vrai remboursement Stripe)
  const choices = (meta?.statuses || []).filter((s) => !["shipped", "refused", "refunded"].includes(s.value));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.patch(`/admin/orders/${order.id}/status`, { status, message: message || null, send_email: sendEmail });
      toast.success("Statut mis à jour");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <ModalShell title="Changer le statut" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <FieldLabel>Nouveau statut</FieldLabel>
          <select
            data-testid="status-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="mt-2 w-full border border-stone rounded-sm p-2.5 text-sm bg-white"
          >
            {choices.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <p className="text-[11px] text-ash mt-1">{`Pour passer en "Expédiée", utilisez l'action `}<em>Définir suivi & expédier</em>.</p>
        </div>
        <div>
          <FieldLabel>Message au client (optionnel)</FieldLabel>
          <textarea
            data-testid="status-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            className="mt-2 w-full border border-stone rounded-sm p-3 text-sm"
            placeholder="Information complémentaire à inclure dans l'email…"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-graphite">
          <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="accent-copper" />
          Envoyer un email au client
        </label>
        <SubmitBtn loading={loading} testid="status-submit">Mettre à jour</SubmitBtn>
      </form>
    </ModalShell>
  );
}

function TrackingModal({ order, meta, onClose, onDone }) {
  const [carrier, setCarrier] = useState(order.carrier || "colissimo");
  const [trackingNumber, setTrackingNumber] = useState(order.tracking_number || "");
  const [trackingUrl, setTrackingUrl] = useState(order.tracking_url || "");
  const [message, setMessage] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.patch(`/admin/orders/${order.id}/tracking`, {
        carrier, tracking_number: trackingNumber, tracking_url: trackingUrl || null,
        message: message || null, send_email: sendEmail,
      });
      toast.success("Suivi enregistré, statut → Expédiée");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <ModalShell title="Définir le suivi & expédier" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <FieldLabel>Transporteur</FieldLabel>
          <select
            data-testid="tracking-carrier"
            value={carrier} onChange={(e) => setCarrier(e.target.value)}
            className="mt-2 w-full border border-stone rounded-sm p-2.5 text-sm bg-white"
          >
            {(meta?.carriers || []).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <FieldLabel>Numéro de suivi</FieldLabel>
          <Input data-testid="tracking-number" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} className="mt-2" required />
        </div>
        <div>
          <FieldLabel>URL de suivi (optionnelle, générée auto si vide)</FieldLabel>
          <Input data-testid="tracking-url" value={trackingUrl} onChange={(e) => setTrackingUrl(e.target.value)} className="mt-2" placeholder="https://…" />
        </div>
        <div>
          <FieldLabel>Message au client (optionnel)</FieldLabel>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="mt-2 w-full border border-stone rounded-sm p-3 text-sm" />
        </div>
        <label className="flex items-center gap-2 text-sm text-graphite">
          <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="accent-copper" />
          {`Envoyer l'email "expédiée" au client`}
        </label>
        <SubmitBtn loading={loading} testid="tracking-submit">Expédier la commande</SubmitBtn>
      </form>
    </ModalShell>
  );
}

function RefuseModal({ order, meta, onClose, onDone }) {
  const [reason, setReason] = useState(meta?.refusal_reasons?.[0]?.value || "other");
  const [message, setMessage] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!window.confirm("Refuser cette commande ? Cette action est définitive (mais elle peut être désarchivée plus tard).")) return;
    setLoading(true);
    try {
      await api.post(`/admin/orders/${order.id}/refuse`, { reason, message: message || null, send_email: sendEmail });
      toast.success("Commande refusée");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <ModalShell title="Refuser la commande" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <FieldLabel>Motif de refus</FieldLabel>
          <select
            data-testid="refuse-reason"
            value={reason} onChange={(e) => setReason(e.target.value)}
            className="mt-2 w-full border border-stone rounded-sm p-2.5 text-sm bg-white"
          >
            {(meta?.refusal_reasons || []).map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        <div>
          <FieldLabel>Message au client (optionnel)</FieldLabel>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} className="mt-2 w-full border border-stone rounded-sm p-3 text-sm" placeholder="Détails ou solution proposée…" />
        </div>
        <label className="flex items-center gap-2 text-sm text-graphite">
          <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="accent-copper" />
          Envoyer l&apos;email au client
        </label>
        <SubmitBtn loading={loading} danger testid="refuse-submit">Refuser</SubmitBtn>
      </form>
    </ModalShell>
  );
}

function RefundModal({ order, onClose, onDone }) {
  const [message, setMessage] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Rembourser intégralement cette commande (${eur(order.total)}) sur Stripe ? Cette action déplace de l'argent réel et est irréversible depuis cette interface.`)) return;
    setLoading(true);
    try {
      await api.post(`/admin/orders/${order.id}/refund`, { message: message || null, send_email: sendEmail });
      toast.success("Commande remboursée sur Stripe");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <ModalShell title="Rembourser la commande" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-sm text-red-700">
          Déclenche un remboursement Stripe réel de <strong>{eur(order.total)}</strong> vers le moyen de paiement du client. Action irréversible depuis cette interface.
        </div>
        <div>
          <FieldLabel>Message au client (optionnel)</FieldLabel>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} className="mt-2 w-full border border-stone rounded-sm p-3 text-sm" placeholder="Motif du remboursement…" />
        </div>
        <label className="flex items-center gap-2 text-sm text-graphite">
          <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="accent-copper" />
          Envoyer l&apos;email au client
        </label>
        <SubmitBtn loading={loading} danger testid="refund-submit">Rembourser sur Stripe</SubmitBtn>
      </form>
    </ModalShell>
  );
}

function MessageModal({ order, meta, onClose, onDone }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);

  const applyTpl = (id) => {
    const tpl = (meta?.message_templates || []).find((t) => t.id === id);
    if (tpl) { setTitle(tpl.title); setBody(tpl.body); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/admin/orders/${order.id}/notify`, { title, body });
      toast.success("Message envoyé au client");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <ModalShell title="Envoyer un message au client" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <FieldLabel>Modèles rapides</FieldLabel>
          <div className="mt-2 flex flex-wrap gap-2">
            {(meta?.message_templates || []).map((t) => (
              <button
                type="button" key={t.id}
                onClick={() => applyTpl(t.id)}
                data-testid={`msg-tpl-${t.id}`}
                className="text-[11px] px-3 py-1.5 border border-stone rounded-sm hover:bg-sand text-graphite"
              >
                {t.title}
              </button>
            ))}
          </div>
        </div>
        <div>
          <FieldLabel>Objet</FieldLabel>
          <Input data-testid="msg-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-2" required />
        </div>
        <div>
          <FieldLabel>Message</FieldLabel>
          <textarea data-testid="msg-body" value={body} onChange={(e) => setBody(e.target.value)} rows={8} className="mt-2 w-full border border-stone rounded-sm p-3 text-sm" required />
        </div>
        <p className="text-[11px] text-ash">L&apos;email sera envoyé à {order.customer_email || order.shipping?.email || order.user_email}.</p>
        <SubmitBtn loading={loading} testid="msg-submit">Envoyer le message</SubmitBtn>
      </form>
    </ModalShell>
  );
}

function NoteModal({ order, onClose, onDone }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/admin/orders/${order.id}/notes`, { text });
      toast.success("Note ajoutée");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <ModalShell title="Ajouter une note interne" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-graphite">Visible uniquement par l&apos;équipe admin, jamais envoyée au client.</p>
        <textarea
          data-testid="note-text"
          value={text} onChange={(e) => setText(e.target.value)} rows={5} required
          className="w-full border border-stone rounded-sm p-3 text-sm"
          placeholder="Note interne…"
        />
        <SubmitBtn loading={loading} testid="note-submit">Ajouter la note</SubmitBtn>
      </form>
    </ModalShell>
  );
}

/* ------------------------------------------------------------------ */
/* Main page                                                           */
/* ------------------------------------------------------------------ */
export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active"); // 'active' | 'archived'
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { archived: tab === "archived" };
      if (statusFilter !== "all") params.status = statusFilter;
      if (search) params.q = search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (minAmount) params.min_amount = parseFloat(minAmount);
      if (maxAmount) params.max_amount = parseFloat(maxAmount);
      const [ordersRes, statsRes] = await Promise.all([
        api.get("/admin/orders", { params }),
        api.get("/admin/orders/stats"),
      ]);
      setOrders(ordersRes.data);
      setStats(statsRes.data);
    } finally { setLoading(false); }
  }, [tab, statusFilter, search, dateFrom, dateTo, minAmount, maxAmount]);

  useEffect(() => { api.get("/admin/orders/meta/options").then(({ data }) => setMeta(data)); }, []);

  // debounced load on filter changes
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const statusMap = useMemo(
    () => Object.fromEntries((meta?.statuses || []).map((s) => [s.value, s.label])),
    [meta],
  );

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/orders/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "commandes-lumiere.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setExporting(false); }
  };

  const resetFilters = () => {
    setStatusFilter("all"); setSearch(""); setDateFrom(""); setDateTo(""); setMinAmount(""); setMaxAmount("");
  };

  const visibleStatuses = (tab === "active" ? ACTIVE_STATUSES : ARCHIVED_STATUSES);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-ash mb-2">Ventes</p>
          <h1 className="font-serif text-4xl text-ink tracking-tight">Commandes</h1>
        </div>
        <button
          onClick={exportCsv}
          disabled={exporting || orders.length === 0}
          data-testid="export-orders-btn"
          className="flex items-center gap-2 border border-ink text-ink px-5 py-3 text-xs uppercase tracking-[0.15em] hover:bg-ink hover:text-cream transition-colors disabled:opacity-50"
        >
          <Download className="h-4 w-4" /> {exporting ? "Export…" : "Exporter CSV"}
        </button>
      </div>

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <Kpi label="Total" value={stats.total} active={tab === "active" && statusFilter === "all"} onClick={() => { setTab("active"); setStatusFilter("all"); }} />
          <Kpi label="Confirmées" value={stats.by_status?.confirmed?.count || 0} active={statusFilter === "confirmed"} onClick={() => { setTab("active"); setStatusFilter("confirmed"); }} />
          <Kpi label="En préparation" value={stats.by_status?.preparation?.count || 0} active={statusFilter === "preparation"} onClick={() => { setTab("active"); setStatusFilter("preparation"); }} />
          <Kpi label="Expédiées" value={stats.by_status?.shipped?.count || 0} active={statusFilter === "shipped"} onClick={() => { setTab("active"); setStatusFilter("shipped"); }} />
          <Kpi label="Archivées" value={stats.archived} active={tab === "archived"} onClick={() => { setTab("archived"); setStatusFilter("all"); }} accent="text-copper" />
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-stone flex mb-5">
        <button
          onClick={() => { setTab("active"); setStatusFilter("all"); }}
          data-testid="tab-active"
          className={`px-5 py-3 text-xs uppercase tracking-[0.15em] border-b-2 ${tab === "active" ? "border-copper text-ink" : "border-transparent text-ash"}`}
        >
          Actives ({stats?.actives ?? "…"})
        </button>
        <button
          onClick={() => { setTab("archived"); setStatusFilter("all"); }}
          data-testid="tab-archived"
          className={`px-5 py-3 text-xs uppercase tracking-[0.15em] border-b-2 ${tab === "archived" ? "border-copper text-ink" : "border-transparent text-ash"}`}
        >
          Archivées ({stats?.archived ?? "…"})
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-6">
        <div className="md:col-span-4 relative">
          <Search className="h-4 w-4 text-ash absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="order-search"
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher n°, nom, email, ville…"
            className="pl-9 rounded-sm border-stone bg-white"
          />
        </div>
        <div className="md:col-span-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="order-status-filter" className="w-full rounded-sm border-stone bg-white text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {visibleStatuses.map((s) => <SelectItem key={s} value={s}>{statusMap[s] || s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Input data-testid="filter-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="md:col-span-2 rounded-sm border-stone bg-white" />
        <Input data-testid="filter-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="md:col-span-2 rounded-sm border-stone bg-white" />
        <Input data-testid="filter-min" type="number" step="0.01" placeholder="Min €" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} className="md:col-span-1 rounded-sm border-stone bg-white" />
        <Input data-testid="filter-max" type="number" step="0.01" placeholder="Max €" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} className="md:col-span-1 rounded-sm border-stone bg-white" />
      </div>
      {(search || statusFilter !== "all" || dateFrom || dateTo || minAmount || maxAmount) && (
        <button onClick={resetFilters} className="text-xs uppercase tracking-[0.15em] text-ash hover:text-ink mb-4">
          Réinitialiser les filtres
        </button>
      )}

      {/* Orders list */}
      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-20 bg-white border border-stone animate-pulse rounded-sm" />)}</div>
      ) : orders.length === 0 ? (
        <div data-testid="admin-no-orders" className="text-center py-20 bg-white border border-stone rounded-sm">
          <Package className="h-10 w-10 text-stone mx-auto" strokeWidth={1} />
          <p className="text-graphite mt-4">Aucune commande ne correspond.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <button
              key={o.id}
              onClick={() => setSelectedId(o.id)}
              data-testid={`admin-order-${o.id.slice(0, 8)}`}
              className="w-full text-left bg-white border border-stone rounded-sm p-4 hover:border-graphite transition-colors flex flex-wrap md:flex-nowrap items-center gap-4"
            >
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-[0.15em] text-ash">#{o.id.slice(0, 8)}</p>
                <p className="font-medium text-ink mt-0.5 truncate">
                  {o.customer_name || o.shipping?.full_name || o.user_email || "Client invité"}
                </p>
                <p className="text-xs text-graphite mt-0.5">
                  {new Date(o.created_at).toLocaleDateString("fr-FR")} · {o.items?.length || 0} article(s) · {o.shipping?.city || "—"}
                  {o.tracking_number && <span className="ml-2 inline-flex items-center gap-1 text-purple-700"><Truck className="h-3 w-3" /> {o.tracking_number}</span>}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-ink">{eur(o.total)}</span>
                <StatusBadge status={o.status} labelMap={statusMap} />
                <ChevronRight className="h-4 w-4 text-ash" />
              </div>
            </button>
          ))}
        </div>
      )}

      {selectedId && (
        <OrderDrawer
          orderId={selectedId}
          meta={meta}
          onClose={() => setSelectedId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
